import { z } from 'zod';

/**
 * Schema for the migrated dashboard state. The existing single-file dashboard
 * stores everything under one `state` blob in localStorage; in the cloud
 * version we keep the same shape (for migration ease) but persist it
 * server-side per Account.
 *
 * Once Phase 1 stabilizes, we'll split sub-objects into proper Postgres tables
 * (clients, deals, decisions, etc.) — but the schema definitions stay here.
 */

export const PipelineStageSchema = z.string(); // "Cold Prospects", "Contacted", etc.

export const ClientStatusSchema = z.enum(['active', 'at-risk', 'inactive', 'prospect']);
export type ClientStatus = z.infer<typeof ClientStatusSchema>;

export const ClientSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: ClientStatusSchema,
  revenue: z.number().default(0),
  costToServe: z.number().default(0),
  hoursLogged: z.number().default(0),
  arBalance: z.number().default(0),
  arBucket: z.enum(['0-30', '31-60', '61-90', '90+']).default('0-30'),
  lastTouch: z.string().nullable(),
  owner: z.string().default(''),
  startDate: z.string().nullable(),
  tags: z.string().default(''),
  notes: z.string().default(''),
});

export type Client = z.infer<typeof ClientSchema>;

export const DealSchema = z.object({
  id: z.string(),
  name: z.string(),
  stage: PipelineStageSchema,
  value: z.number().default(0),
  expectedClose: z.string().nullable(),
  lastTouch: z.string().nullable(),
  stageEnteredAt: z.string().nullable(),
  source: z.string().default(''),
  owner: z.string().default(''),
  notes: z.string().default(''),
  closedAt: z.string().nullable(),
  wonReason: z.string().nullable(),
  lostReason: z.string().nullable(),
});

export type Deal = z.infer<typeof DealSchema>;

export const KpiMetricSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.string().default(''),
  target: z.string().default(''),
  unit: z.string().default(''),
  format: z.enum(['number', 'currency', 'percent']).default('number'),
  invert: z.boolean().default(false),
  pinned: z.boolean().default(false),
});

export type KpiMetric = z.infer<typeof KpiMetricSchema>;

export const DepartmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(['green', 'amber', 'red', 'dim']),
  health: z.number().min(0).max(100),
  summary: z.string(),
  owner: z.string().default(''),
  tools: z.array(z.string()).default([]),
  integrations: z
    .array(z.object({ name: z.string(), connected: z.boolean() }))
    .default([]),
  kpis: z.array(z.object({
    id: z.string(),
    label: z.string(),
    current: z.number(),
    target: z.number(),
  })).default([]),
  journal: z.string().default(''),
  lastReviewedAt: z.string().nullable(),
});

export type Department = z.infer<typeof DepartmentSchema>;

/**
 * Top-level dashboard state object. Mirrors the localStorage shape of the
 * existing single-file dashboard for clean migration.
 */
export const DashboardStateSchema = z.object({
  schemaVersion: z.number(),
  clients: z.array(ClientSchema).default([]),
  deals: z.array(DealSchema).default([]),
  departments: z.array(DepartmentSchema).default([]),
  pipeline: z.record(z.string(), z.number()).default({}),
  // We'll add more as we migrate. For now, accept arbitrary additional
  // fields to preserve user data during the transition period.
}).passthrough();

export type DashboardState = z.infer<typeof DashboardStateSchema>;
